import { useMemo, useRef } from 'react';

import { useMutation } from '@tanstack/react-query';
import { type RowSelectionState } from '@tanstack/react-table';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { type ExposedFormMethods, type FormData, EditRecordingForm } from './EditRecordingForm';
import { batchUpdateRecordings, type BatchUpdateRecordingsData } from '../../requests/batchUpdateRecordings';
import {
    type RecordingBulkPatchItem,
    type ClassRecording,
    type Permission,
    type RecordingRestrictMode,
    type RecordingRestrictionsRecord
} from '../classroom/types';
import { type BulkActionDialogProps } from '../classroom/ui-types';
import { ConfirmationDialog } from '../common/dialog/ConfirmationDialog';

export type BulkEditPermissionDialogProps = Omit<BulkActionDialogProps, 'recording'>;

export const BulkEditPermissionDialog = (props: BulkEditPermissionDialogProps) => {
    const { t } = useTranslation();
    const { open, recordings, setRecordings, rowSelection, setRowSelection, onClose, classUuid, userUuid } = props;
    const { enqueueSnackbar } = useSnackbar();
    const editFormRef = useRef<ExposedFormMethods>(null);

    const countSelectedRecordings = useMemo(
        () => (!rowSelection ? 0 : Object.values(rowSelection).filter((s) => Boolean(s)).length),
        [rowSelection]
    );

    const handleClose = () => {
        onClose?.({}, 'backdropClick');
    };

    const mutation = useMutation({
        mutationFn: async (data: BatchUpdateRecordingsData) => batchUpdateRecordings(data),
        onSuccess: () => {
            handleClose();
            enqueueSnackbar(t('lti:recordings.bulk-actions.update-success'), {
                variant: 'success'
            });
        },
        onError: () => {
            enqueueSnackbar(t('lti:recordings.bulk-actions.errors.update-failed'), {
                variant: 'error'
            });
        }
    });

    const { isLoading: confirming } = mutation;

    const handleDialogConfirmed = () => {
        editFormRef?.current?.triggerSubmit();
    };

    const onSubmitFormValid = async (data: FormData) => {
        const recordingUuidsToUpdate = Object.keys(rowSelection as RowSelectionState).filter(
            (key) => rowSelection![key]
        );

        if (!userUuid || !recordingUuidsToUpdate.length) {
            console.error('Missing required data.');
            return;
        }

        const updatedRecordings: ClassRecording[] = recordings.map((r) => {
            if (!recordingUuidsToUpdate.includes(r.recordingId)) {
                return r;
            }

            const lmsGroups = !data.studentGroups?.length
                ? []
                : data.studentGroups.map((sg) => ({
                      studentGroupId: sg
                  }));

            return {
                ...r,
                restrictions: {
                    recording_uuid: r.recordingId,
                    ...r.restrictions,
                    mode: data.restrictBy,
                    lms_groups: lmsGroups
                } as RecordingRestrictionsRecord,
                permission: data.permission as Permission
            };
        });
        setRecordings(updatedRecordings);

        const recordingPatchItems: RecordingBulkPatchItem[] = recordings
            .filter((r) => recordingUuidsToUpdate.includes(r.recordingId))
            .map((r) => ({
                recordingId: r.recordingId,
                name: r.name, // Name is never edited in bulk, hence we use the current name.
                permission: data.permission as Permission,
                restrictBy: data.restrictBy as RecordingRestrictMode,
                studentGroups: data.studentGroups
            }));

        const patchData: BatchUpdateRecordingsData = { userUuid, classUuid, recordings: recordingPatchItems };
        await mutation.mutateAsync(patchData);

        if (setRowSelection) {
            setRowSelection({});
        }
        handleClose();
    };

    return (
        <ConfirmationDialog
            id="edit-recording-permission"
            open={open}
            onClose={handleClose}
            title={t('lti:recordings.bulk-actions.edit-recordings', { count: countSelectedRecordings })}
            confirmLabel={t('common:save')}
            onConfirm={handleDialogConfirmed}
            confirming={confirming}
        >
            <EditRecordingForm
                ref={editFormRef}
                onSubmitValid={onSubmitFormValid}
                showNameField={false}
                dialogOpen={open}
            />
        </ConfirmationDialog>
    );
};
